import { useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Download, Database, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBackupPage() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const handleBackup = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/backup/data`);
      const data = response.data;
      setStats(data.stats);

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `san_security_backup_${format(new Date(), 'yyyy-MM-dd_HHmm')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Backup downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        <div className="border-b border-border pb-4">
          <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <Database className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={1.5} />
            DATABASE BACKUP
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Export all application data as a JSON backup file
          </p>
        </div>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-8 text-center space-y-6">
            <Database className="w-16 h-16 mx-auto text-primary opacity-80" />
            <div>
              <h2 className="text-lg font-medium text-foreground">Full Data Export</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                Download a complete backup of all users, shifts, clock events, policies, and notifications.
                Photos are excluded to keep file size manageable.
              </p>
            </div>
            <Button
              onClick={handleBackup}
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-lg"
              data-testid="download-backup-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              {loading ? 'Generating Backup...' : 'Download Backup'}
            </Button>
          </CardContent>
        </Card>

        {stats && (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="font-medium text-foreground">Last Backup Summary</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(stats).map(([key, value]) => (
                  <div key={key} className="bg-background border border-border rounded-sm p-3 text-center">
                    <p className="text-2xl font-bold text-primary">{value}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                      {key.replace(/_/g, ' ')}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
